import { useCallback, useState } from 'react'
import { toast } from '@sales-center/components'
import { CreateNewProps } from '../typings'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { LocationMatch, matchCreateNew } from '@user/store/entityLocations'
import { useAppSelector } from '@user/store/hooks'
import {
  APIError,
  ENTITY_TYPES,
  isAPIError,
  isAPIValidationError,
  PestPacTimeoutExpirationErrorMessage,
  PestPacTimeoutExpirationStatus,
  validationErrorsToMap,
} from '@sales-center/common'
import { selectLeadDetail } from '@user/store/lead/slice'
import { selectOpportunityDetail } from '@user/store/opportunity/slice'
import { getOpportunityDetail } from '@user/store/opportunity'
import { getLeadDetail } from '@user/store/lead'
import { useUpdateNoteList } from '@user/components/Notes/EntityNotes/hooks/useUpdateNoteList'
import { useGetNoteSetting } from '@user/components/Notes/EntityNotes/hooks/useGetNoteSetting'
import {
  EMAIL_VALIDATION_PREFIX,
  parseEmailValidationErrorMessages,
} from '@user/store/emailValidation'

export function useActions(params: CreateNewProps) {
  const {
    entityId,
    entityType,
    billToLocations,
    serviceLocations,
    onClose,
    variant,
  } = params

  const dispatch = useAppDispatch()
  const detail = useAppSelector((state) =>
    entityType === ENTITY_TYPES.Lead
      ? selectLeadDetail(state)
      : selectOpportunityDetail(state),
  )
  const [saving, setSaving] = useState(false)
  const [errors, setError] = useState<string | null>(null)

  const { onUpdateNoteLIst } = useUpdateNoteList(entityId, entityType)
  const { fetchNoteSettings, fetchNoteCodes } = useGetNoteSetting()

  const onSave = useCallback(async () => {
    const locations = [
      ...billToLocations,
      ...serviceLocations,
    ].map<LocationMatch>(({ id, billingLocation }) => ({
      locationId: id,
      externalEntityId: '',
      externalEntityType: billingLocation ? 'BillTo' : 'Location',
      createNewLocation: true,
    }))

    setSaving(true)

    const result = await dispatch(
      matchCreateNew({
        entityType,
        data: {
          id: entityId,
          locations,
          entityType,
        },
        detail: params.detail,
      }),
    )

    setSaving(false)

    if (matchCreateNew.fulfilled.match(result)) {
      onUpdateNoteLIst()

      const [noteSettings, noteCodes] = await Promise.all([
        fetchNoteSettings(),
        fetchNoteCodes(),
      ])

      const hasNotesEnabled =
        noteSettings.noteAutomationEnabled &&
        noteCodes.some((x) => x.id === noteSettings.defaultNoteCodeId)

      const message = hasNotesEnabled
        ? 'Location Created. Any Sales Center notes have been added to the PestPac location(s).'
        : 'Locations Created.'

      toast.success(message)

      if (detail?.id == entityId) {
        if (entityType === ENTITY_TYPES.Opportunity) {
          dispatch(getOpportunityDetail(entityId))
        } else {
          dispatch(getLeadDetail(entityId))
        }
      }
      onClose()
    } else {
      let errMessage = 'Unable to Create Locations'

      if (result.payload) {
        if (isAPIValidationError(result.payload)) {
          const errors = validationErrorsToMap(result.payload)
          if (errors) {
            errMessage = [...errors.values()].flat().join(',')
          }
        } else if (isAPIError(result.payload)) {
          if (result.payload.StatusCode === PestPacTimeoutExpirationStatus) {
            errMessage = PestPacTimeoutExpirationErrorMessage
          } else if (
            result.payload.Message?.includes(EMAIL_VALIDATION_PREFIX)
          ) {
            const splitMessages = parseEmailValidationErrorMessages(
              result.payload.Message,
            )

            if (splitMessages.length) {
              splitMessages.forEach((message) => toast.error(message))
              errMessage = splitMessages.join('\n')
              setError(errMessage)
              return
            }
          } else {
            errMessage = result.payload.Message
          }
        } else {
          const error = result.payload as APIError

          if (error && error.StatusCode === PestPacTimeoutExpirationStatus) {
            errMessage = PestPacTimeoutExpirationErrorMessage
          }
        }
      }
      toast.error(errMessage)
      setError(errMessage)
    }
  }, [
    billToLocations,
    serviceLocations,
    dispatch,
    entityType,
    entityId,
    onClose,
    detail?.id,
    onUpdateNoteLIst,
    variant,
    fetchNoteSettings,
    fetchNoteCodes,
  ])

  return { saving, onSave, dispatch, errors }
}
